/* iter-tips-editor — Admin CRUD for `help_tips`.
 *
 * Ships as a first-class admin tab so ops no longer needs a Mongo
 * shell to author instructional popover content. Each row keys off
 * a slug; the frontend `<HelpTip slug="…">` resolves against this
 * table (via `/api/help/tips`) on every render.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Plus, Save, Trash2, RefreshCw, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { openConfirm } from '@/components/admin/ConfirmDialog';

const EMPTY_DRAFT = { slug: '', title: '', content: '', enabled: true, visible_from: '', visible_until: '' };

export default function TipsAdminPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [editingSlug, setEditingSlug] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.tips.list();
      setRows(r?.tips || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load tips.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.slug || '').toLowerCase().includes(term) ||
      (r.title || '').toLowerCase().includes(term) ||
      (r.content || '').toLowerCase().includes(term),
    );
  }, [q, rows]);

  const startCreate = () => { setEditingSlug('__new__'); setDraft(EMPTY_DRAFT); };
  const startEdit = (row) => {
    setEditingSlug(row.slug);
    setDraft({
      slug: row.slug,
      title: row.title || '',
      content: row.content || '',
      enabled: row.enabled !== false,
      visible_from:  row.visible_from  ? row.visible_from.slice(0, 16)  : '',
      visible_until: row.visible_until ? row.visible_until.slice(0, 16) : '',
    });
  };
  const cancelEdit = () => { setEditingSlug(null); setDraft(EMPTY_DRAFT); };

  const save = async () => {
    if (!draft.slug.trim() || !draft.content.trim()) {
      toast.error('Slug and Content are required.'); return;
    }
    setSaving(true);
    try {
      // iter-tip-scheduling — convert HTML datetime-local (no TZ) to ISO.
      const toIso = (v) => (v ? new Date(v).toISOString() : null);
      const payload = {
        title: draft.title,
        content: draft.content,
        enabled: draft.enabled,
        visible_from:  toIso(draft.visible_from),
        visible_until: toIso(draft.visible_until),
      };
      if (editingSlug === '__new__') {
        await adminApi.tips.create({ slug: draft.slug, ...payload });
        toast.success('Tip created.');
      } else {
        await adminApi.tips.update(editingSlug, payload);
        toast.success('Tip updated.');
      }
      cancelEdit();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!(await openConfirm({
      title: `Delete tip '${row.slug}'?`,
      description: 'This cannot be undone.',
      tone: 'destructive',
    }))) return;
    try {
      await adminApi.tips.remove(row.slug);
      toast.success('Tip deleted.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-tips-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-[14px]">Instructional Tips</CardTitle>
            <p className="text-[11.5px] text-muted-foreground">
              Author the popover content served by every <code>&lt;HelpTip slug="…"&gt;</code>.
              Changes push to the live cache immediately after save.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="admin-tips-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={startCreate} disabled={editingSlug !== null} data-testid="admin-tips-new">
              <Plus className="h-3.5 w-3.5" /> New tip
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Filter by slug, title, or body…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="mb-3 h-8 text-[12px]"
            data-testid="admin-tips-filter"
          />

          {editingSlug && (
            <div className="mb-4 rounded-md border border-primary/40 bg-primary/5 p-3" data-testid="admin-tips-editor">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[12px] font-semibold">
                  {editingSlug === '__new__' ? 'New tip' : `Editing '${editingSlug}'`}
                </div>
                <Button size="sm" variant="ghost" onClick={cancelEdit} data-testid="admin-tips-cancel">
                  <X className="h-3.5 w-3.5" /> Cancel
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label className="text-[11px]">Slug</Label>
                  <Input
                    value={draft.slug}
                    onChange={(e) => setDraft(d => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9\-_.]/g, '') }))}
                    disabled={editingSlug !== '__new__'}
                    placeholder="editor.suggestions-drawer"
                    className="h-8 font-mono-stat text-[12px]"
                    data-testid="admin-tips-slug"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Title (optional)</Label>
                  <Input
                    value={draft.title}
                    onChange={(e) => setDraft(d => ({ ...d, title: e.target.value }))}
                    placeholder="Suggestions Drawer"
                    className="h-8 text-[12px]"
                    data-testid="admin-tips-title"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-[11px]">Content</Label>
                <Textarea
                  value={draft.content}
                  onChange={(e) => setDraft(d => ({ ...d, content: e.target.value }))}
                  rows={5}
                  placeholder="Anchored notes editors leave on any paragraph…"
                  className="text-[12px]"
                  data-testid="admin-tips-content"
                />
              </div>
              {/* iter-tip-scheduling — optional visibility window. */}
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label className="text-[11px]">Visible from (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={draft.visible_from}
                    onChange={(e) => setDraft(d => ({ ...d, visible_from: e.target.value }))}
                    className="h-8 text-[12px]"
                    data-testid="admin-tips-visible-from"
                  />
                </div>
                <div>
                  <Label className="text-[11px]">Visible until (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={draft.visible_until}
                    onChange={(e) => setDraft(d => ({ ...d, visible_until: e.target.value }))}
                    className="h-8 text-[12px]"
                    data-testid="admin-tips-visible-until"
                  />
                </div>
              </div>
              <p className="mt-1 text-[10.5px] text-muted-foreground">
                Leave blank for open-ended. Seasonal callouts auto-appear at "from" and retire at "until" — no manual toggle needed.
              </p>
              <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2 text-[11.5px]">
                  <Switch
                    checked={draft.enabled}
                    onCheckedChange={(v) => setDraft(d => ({ ...d, enabled: v }))}
                    data-testid="admin-tips-enabled"
                  />
                  Enabled (shown to users)
                </label>
                <Button size="sm" onClick={save} disabled={saving} data-testid="admin-tips-save">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  {' '}{editingSlug === '__new__' ? 'Create' : 'Save'}
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-[12px] text-muted-foreground">
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-muted-foreground" data-testid="admin-tips-empty">
              {q ? 'No tips match your filter.' : 'No tips yet. Click "New tip" to author your first one.'}
            </div>
          ) : (
            <div className="divide-y divide-border rounded-md border border-border" data-testid="admin-tips-list">
              {filtered.map((row) => (
                <div key={row.slug} className="flex items-start gap-3 p-3" data-testid={`admin-tips-row-${row.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="font-mono-stat text-[11.5px] text-primary">{row.slug}</code>
                      {row.enabled === false && (
                        <span className="rounded-sm bg-muted px-1.5 text-[9.5px] uppercase text-muted-foreground">Disabled</span>
                      )}
                    </div>
                    {row.title && <div className="mt-1 text-[12.5px] font-semibold">{row.title}</div>}
                    <div className="mt-1 line-clamp-2 text-[11.5px] text-muted-foreground">{row.content}</div>
                    {row.updated_at && (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        Updated {new Date(row.updated_at).toLocaleString()}
                        {row.visible_from && (
                          <> · from {new Date(row.visible_from).toLocaleString()}</>
                        )}
                        {row.visible_until && (
                          <> · until {new Date(row.visible_until).toLocaleString()}</>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(row)} data-testid={`admin-tips-edit-${row.slug}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(row)} className="text-destructive" data-testid={`admin-tips-delete-${row.slug}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
